import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { api } from "@/lib/api";

const COPY = {
  network: "Network error — please retry.",
  generic: "Something went wrong — please retry.",
  request_rejected: "Request rejected.",
  workspace_invalid: "Workspace unavailable — please refresh.",
  workspace_denied: "Access denied for this workspace.",
  session_ended: "Previous session ended — please resend your question.",
  rate_limited: "You're sending questions too fast — try again in a moment.",
  retrieval_not_configured: "AI knowledge base is not configured for this workspace.",
  ai_unavailable: "AI provider is temporarily unavailable — please retry.",
  retrieval_unavailable: "Retrieval is temporarily unavailable — please retry.",
  too_long: "Question is too long (maximum 2048 characters).",
  empty: "Please enter a question.",
};

function isCancel(err) {
  return (
    (axios.isCancel && axios.isCancel(err)) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError"
  );
}

// Returns { ok:true, value:normalized } | { ok:false }
export function validateAndNormalize(data) {
  if (!data || typeof data !== "object") return { ok: false };
  if (typeof data.session_id !== "string" || data.session_id.length === 0) return { ok: false };
  if (typeof data.answered !== "boolean") return { ok: false };
  const fb = data.fallback_from;
  const fbOk = fb === null || fb === undefined || fb === "dense" || fb === "sparse";
  if (!fbOk) return { ok: false };
  if (typeof data.degraded !== "boolean") return { ok: false };
  if (data.answered === true) {
    if (typeof data.answer !== "string") return { ok: false };
    if (data.answer_language !== "ar" && data.answer_language !== "en") return { ok: false };
    if (!Array.isArray(data.citations)) return { ok: false };
    for (const c of data.citations) {
      if (!c || typeof c !== "object") return { ok: false };
      if (typeof c.ref !== "string") return { ok: false };
      if (!Number.isInteger(c.chunk_index) || c.chunk_index < 0) return { ok: false };
      if (typeof c.document_title !== "string") return { ok: false };
      if (typeof c.score !== "number" || !Number.isFinite(c.score)) return { ok: false };
      if (typeof c.snippet_text !== "string") return { ok: false };
    }
    return {
      ok: true,
      value: {
        session_id: data.session_id,
        answered: true,
        answer: data.answer,
        answer_language: data.answer_language,
        degraded: data.degraded,
        citations: data.citations.map((c) => ({
          ref: c.ref,
          document_title: c.document_title,
          chunk_index: c.chunk_index,
          score: c.score,
          snippet_text: c.snippet_text,
        })),
      },
    };
  }
  if (data.answered === false) {
    if (data.reason !== "insufficient_sources" && data.reason !== "citation_validation_failed") return { ok: false };
    if (!Array.isArray(data.citations)) return { ok: false };
    return {
      ok: true,
      value: {
        session_id: data.session_id,
        answered: false,
        reason: data.reason,
        degraded: data.degraded,
      },
    };
  }
  return { ok: false };
}

function classify(err) {
  if (!err.response) return { kind: "network" };
  const { status, data } = err.response;
  const d = data && typeof data === "object" ? data.detail : undefined;
  if (status === 422 && Array.isArray(d)) return { kind: "validation", status };
  if (d && typeof d === "object" && !Array.isArray(d) && typeof d.error === "string") {
    return { kind: "coded", status, code: d.error };
  }
  return { kind: "opaque", status };
}

export function useAssistantAnswer() {
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);
  const nextTurnIdRef = useRef(1);

  const [workspaceState, setWorkspaceState] = useState("loading");
  const [workspaceId, setWorkspaceId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [inputDraft, setInputDraft] = useState("");
  const [history, setHistory] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [inputError, setInputError] = useState("");
  const [persistentAlert, setPersistentAlert] = useState(null); // {title, body, variant}

  useEffect(() => {
    mountedRef.current = true;
    const wsController = new AbortController();
    (async () => {
      let data = null, failed = false;
      try {
        const resp = await api.get("/me/workspace", { signal: wsController.signal });
        data = resp.data;
      } catch (err) {
        if (isCancel(err)) return;
        failed = true;
      }
      if (!mountedRef.current) return;
      if (wsController.signal.aborted) return;
      if (failed) { setWorkspaceState("unavailable"); return; }
      if (data && typeof data.workspace_id === "string" && data.workspace_id.length > 0) {
        setWorkspaceId(data.workspace_id);
        setWorkspaceState("ready");
      } else {
        setWorkspaceState("unavailable");
      }
    })();
    return () => {
      mountedRef.current = false;
      wsController.abort();
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const handleClassifiedError = useCallback((err) => {
    const c = classify(err);
    if (c.kind === "network") return void toast.error(COPY.network);
    if (c.status === 401) return; // interceptor handles sign-out; silent locally
    if (c.kind === "validation") return void toast.error(COPY.request_rejected);
    if (c.kind === "coded") {
      switch (c.code) {
        case "retrieval_query_empty": setInputError(COPY.empty); return;
        case "retrieval_query_too_long": setInputError(COPY.too_long); return;
        case "retrieval_top_k_out_of_range":
        case "retrieval_filters_invalid":
          return void toast.error(COPY.request_rejected);
        case "workspace_id_invalid":
          return void toast.error(COPY.workspace_invalid);
        case "workspace_access_denied":
          setWorkspaceState("unavailable");
          setPersistentAlert({ title: "Access denied", body: COPY.workspace_denied, variant: "destructive" });
          return;
        case "conversation_not_found":
          setSessionId(null);
          toast.error(COPY.session_ended);
          return;
        case "retrieval_not_configured":
          setPersistentAlert({ title: "Not configured", body: COPY.retrieval_not_configured, variant: "default" });
          return;
        case "ai_temporarily_unavailable":
          return void toast.error(COPY.ai_unavailable);
        case "retrieval_embed_unavailable":
        case "retrieval_database_temporary_failure":
          return void toast.error(COPY.retrieval_unavailable);
        default:
          return void toast.error(COPY.generic);
      }
    }
    if (c.status === 429) return void toast.error(COPY.rate_limited);
    return void toast.error(COPY.generic);
  }, []);

  const send = useCallback(async () => {
    if (inFlightRef.current) return;
    if (workspaceState !== "ready") return;
    if (typeof workspaceId !== "string" || workspaceId.length === 0) return;
    const submittedQuestion = inputDraft;
    if (!submittedQuestion.trim() || isSending) return;

    inFlightRef.current = true;
    setInputError("");
    const controller = new AbortController();
    abortRef.current = controller;
    if (mountedRef.current) setIsSending(true);

    const url = `/workspaces/${workspaceId}/assistant/answer`;
    const payload = sessionId
      ? { question: submittedQuestion, session_id: sessionId, mode: "hybrid", top_k: 8 }
      : { question: submittedQuestion, mode: "hybrid", top_k: 8 };

    try {
      const { data } = await api.post(url, payload, { signal: controller.signal });
      if (!mountedRef.current) return;
      if (controller.signal.aborted) return;
      if (abortRef.current !== controller) return;
      const v = validateAndNormalize(data);
      if (!v.ok) { toast.error(COPY.generic); return; }
      const assignedTurnId = nextTurnIdRef.current++;
      setHistory((prev) => [...prev, { turnId: assignedTurnId, question: submittedQuestion, response: v.value }]);
      setSessionId(v.value.session_id);
      setInputDraft("");
    } catch (err) {
      if (isCancel(err)) return;
      if (!mountedRef.current) return;
      if (abortRef.current !== controller) return;
      handleClassifiedError(err);
    } finally {
      const isCurrentRequest = abortRef.current === controller;
      if (isCurrentRequest) {
        abortRef.current = null;
        inFlightRef.current = false;
        if (mountedRef.current) setIsSending(false);
      }
    }
  }, [workspaceState, workspaceId, inputDraft, sessionId, isSending, handleClassifiedError]);

  return {
    workspaceState, workspaceId,
    sessionId, history, isSending,
    inputDraft, setInputDraft,
    inputError, persistentAlert,
    send,
  };
}
